import { Application, Graphics, Sprite } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';
import { destroyPixiApp } from '../core/destroyPixiApp';
import { DEFAULT_THEME, type Theme } from '../theme';
import { isDestroyed, type SceneState } from './SceneLifecycle';

export interface SceneBaseOptions {
  /** Render the default vertical-gradient background using the theme
   * palette. Default true. Set false for scenes that paint their own. */
  drawDefaultBackground?: boolean;
  /** Theme used for the default background gradient and inherited by
   * subclasses (palette → widget colours). Defaults to {@link DEFAULT_THEME}. */
  theme?: Theme;
}

/**
 * Common Pixi-app + lifecycle plumbing shared by every demo scene.
 *
 * <p>Subclasses override:
 * <ul>
 *   <li>{@link buildContent} — add machine, components, etc. after the app is
 *       ready;</li>
 *   <li>{@link onResize} — react to renderer resize (called once after init,
 *       then on every resize event);</li>
 *   <li>{@link onKeyDown} — handle window keydown events;</li>
 *   <li>{@link disposeResources} — drop subscriptions / destroy components.</li>
 * </ul>
 *
 * <p>Handles the React 19 strict-mode + async-init race transparently: if
 * {@link destroy} is called while {@link init}'s {@code await app.init()} is
 * still in flight, the scene tears down the orphaned Pixi app and bails out
 * cleanly without ever appending a canvas to a detached container.
 */
export abstract class SceneBase {
  protected app: Application;
  protected state: SceneState = 'created';
  protected readonly theme: Theme;
  private resizeHandler: (() => void) | null = null;
  private keydownHandler: ((event: KeyboardEvent) => void) | null = null;

  constructor(
    protected readonly container: HTMLElement,
    private readonly options: SceneBaseOptions = {},
  ) {
    this.app = new Application();
    this.theme = options.theme ?? DEFAULT_THEME;
  }

  async init(): Promise<void> {
    if (this.state !== 'created') return;
    this.state = 'initializing';

    await this.app.init({
      resizeTo: this.container,
      backgroundAlpha: 0,
      antialias: true,
      resolution: window.devicePixelRatio,
      autoDensity: true,
    });

    if (isDestroyed(this.state)) {
      destroyPixiApp(this.app, this.constructor.name);
      return;
    }

    // Subclass preload hook — themes with sprite/audio assets register +
    // load them here before content build. Bail out cleanly if the scene
    // was destroyed during the await (same strict-mode race as app.init).
    await this.preload();
    if (isDestroyed(this.state)) {
      destroyPixiApp(this.app, this.constructor.name);
      return;
    }

    this.container.appendChild(this.app.canvas);
    this.app.canvas.classList.add('block', 'h-full', 'w-full');

    if (this.options.drawDefaultBackground !== false) {
      this.buildDefaultBackground();
    }
    this.buildContent();

    this.resizeHandler = () => this.onResize();
    this.app.renderer.on('resize', this.resizeHandler);
    this.onResize();

    this.keydownHandler = (event: KeyboardEvent) => this.onKeyDown(event);
    window.addEventListener('keydown', this.keydownHandler);

    this.state = 'initialized';
  }

  destroy(): void {
    if (this.state === 'destroyed') return;
    const wasInitialized = this.state === 'initialized';
    this.state = 'destroyed';

    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }
    this.disposeResources(wasInitialized);
    if (!wasInitialized) return;

    if (this.resizeHandler) {
      this.app.renderer.off('resize', this.resizeHandler);
      this.resizeHandler = null;
    }
    // Never destroy(true): another scene or board may still be drawing
    // (core/destroyPixiApp).
    destroyPixiApp(this.app, this.constructor.name);
  }

  /** Subclass hook — called after the Pixi app + default background are
   * ready, in the {@code 'initializing'} state. Subclasses add their
   * machine, components, and initial event subscriptions here. */
  protected abstract buildContent(): void;

  /** Subclass hook — called after the Pixi app finishes init but before
   * {@link buildContent}. Use to async-load theme asset bundles or other
   * pre-render resources. Default no-op; awaits resolve immediately. */
  protected preload(): Promise<void> {
    return Promise.resolve();
  }

  /** Subclass hook — called once after init and on every renderer resize.
   * Default no-op; override to layout components. */
  protected onResize(): void {
    // no-op
  }

  /** Subclass hook — called on every window keydown while the scene is
   * mounted. Default no-op. */
  protected onKeyDown(_event: KeyboardEvent): void {
    // no-op
  }

  /** Subclass hook — drop subscriptions, destroy components, free resources.
   * Called from {@link destroy} before the Pixi app itself is destroyed.
   * If {@code wasInitialized} is false, content was never built — only
   * model state needs disposing. */
  protected abstract disposeResources(wasInitialized: boolean): void;

  private buildDefaultBackground(): void {
    // 1) Always paint the gradient first — acts as a fallback when the
    //    background image fails to load and as the colour wash behind
    //    semi-transparent backgrounds.
    const bg = new Graphics();
    this.app.stage.addChild(bg);
    const top = this.theme.palette.bgGradientTop;
    const bot = this.theme.palette.bgGradientBottom;
    const r0 = (top >> 16) & 0xff;
    const g0 = (top >> 8) & 0xff;
    const b0 = top & 0xff;
    const r1 = (bot >> 16) & 0xff;
    const g1 = (bot >> 8) & 0xff;
    const b1 = bot & 0xff;
    const drawBg = (): void => {
      const { width, height } = this.app.renderer;
      bg.clear();
      const stripes = 24;
      for (let i = 0; i < stripes; i++) {
        const t = i / stripes;
        const r = Math.round(r0 + (r1 - r0) * t);
        const g = Math.round(g0 + (g1 - g0) * t);
        const b = Math.round(b0 + (b1 - b0) * t);
        bg.rect(0, (height * i) / stripes, width, height / stripes).fill({
          color: (r << 16) | (g << 8) | b,
        });
      }
    };
    drawBg();
    this.app.renderer.on('resize', drawBg);

    // 2) Optional: full-bleed theme background image, drawn cover-fit
    //    on top of the gradient. Loaded by AssetLoader.loadTheme(); if
    //    the texture isn't there yet we silently skip and the gradient
    //    stands alone.
    const bgEntry = this.theme.assets?.scene?.background;
    if (!bgEntry) return;
    const tex = AssetLoader.shared.getTexture(bgEntry.alias);
    if (!tex) return;
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    this.app.stage.addChild(sprite);
    const fitBg = (): void => {
      const { width, height } = this.app.renderer;
      sprite.x = width / 2;
      sprite.y = height / 2;
      // Cover-fit: scale to fill, cropping the longer axis.
      const scale = Math.max(
        width / sprite.texture.width,
        height / sprite.texture.height,
      );
      sprite.scale.set(scale);
    };
    fitBg();
    this.app.renderer.on('resize', fitBg);
  }
}
